import TextButtonIcon from "@@/components/Button/TextButtonIcon";
import SearchCardsContainer from "@@/components/ListingPage/Search/Cards";
import HelpMessage from "@@/components/ListingPage/Search/HelpMessage";
import Scrollbar from "@@/components/Scrollbar";
import SearchInput from "@@/components/SearchInput/MainMenu";
import Box from "@@/elements/Box";
import { ArrowRight } from "@@/elements/Icons/icons";
import Typhography from "@@/elements/Typography";
import { ScreenSize, useIsBreakpointOrBelow } from "@@/hooks/device/use-breakpoint";
import { LinkData } from "@@/models/linkdata";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import useFetchMainMenuSearchData from "./useFetchMainMenuSearch";

type Props = {
  open: boolean;
  setOpen: Function;
  searchData: {
    header: string;
    searchLink: LinkData;
    placeholder: string;
    totalMatchesLabel: string;
    noMatchesMessage: string;
    noResultMessage: string;
    noSearchMadeMessage: string;
    searchRequest: {
      language: string;
    }
  }
};

const MainMenuSearch = ({
  open,
  setOpen,
  searchData,
}: Props) => {
  const {
    header,
    searchLink,
    placeholder,
    totalMatchesLabel,
    noMatchesMessage,
    noResultMessage,
    noSearchMadeMessage,
    searchRequest,
  } = searchData;

  const ref = useRef(null);
  const searchClassNames = cn(styles.search, {
    [styles.search__open]: open,
  });
  const [visibleItems, setVisibleItems] = useState(5);
  const [input, setInput] = useState("");
  const { data, error, loading } = useFetchMainMenuSearchData(input, searchRequest.language);

  const showResults = (data && !error) || loading;
  const showHelpMessage = data && !data.items?.length && !error && !loading;

  const showMoreButton = !loading && !!data?.items?.length && searchLink;

  const url = `${searchLink?.href}?q=${input}`;

  const redirect = () => {
    window.location.href = url;
  };

  const returnFocusToSearchButton = () => {
    const searchButton = document.getElementById("search_button");
    searchButton?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchButton = document.getElementById("search_button");
      if (searchButton?.contains(event.target) || !open) return;
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false, event);
        returnFocusToSearchButton();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        setOpen(false);
        returnFocusToSearchButton();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [ref, open]);

  const onClose = () => {
    setOpen(false);
    returnFocusToSearchButton();
  };

  useEffect(() => {
    if (!open) setInput("");
  }, [open]);

  const isMobileDevice = useIsBreakpointOrBelow(ScreenSize.MEDIUM);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [scrollbarMaxHeight, setScrollbarMaxHeight] = useState<string>("none");

  useEffect(() => {
    const windowListener = () => {
      const { clientHeight } = document.documentElement;
      if (isMobileDevice) setVisibleItems(5);
      else if (clientHeight < 900) setVisibleItems(1);
      else if (clientHeight < 1000) setVisibleItems(2);
      else if (clientHeight < 1200) setVisibleItems(3);
      else if (clientHeight < 1400) setVisibleItems(4);
      else if (clientHeight < 1600) setVisibleItems(5);
      else setVisibleItems(6);
    }


    const updateMaxHeight = () => {
      if (!scrollAreaRef.current || !open) return;
      const rect = scrollAreaRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top;
      setScrollbarMaxHeight(`${Math.max(available, 150)}px`);
    };

    updateMaxHeight();
    windowListener();
    window.addEventListener("resize", updateMaxHeight);
    window.addEventListener("resize", windowListener);

    return () => {
      window.removeEventListener("resize", updateMaxHeight);
      window.removeEventListener("resize", windowListener);
    };
  }, [open, showResults]);

  return (
    <div
      className={styles.search_wrapper}
      ref={ref}
      id="search-wrapper">
      <div className={searchClassNames} inert={!open ? "" : undefined}>
        <Box className={styles.search__content}>
          {header && (
            <Typhography
              className={styles.search__content__header}
              variant="heading1"
              asElement="div"
              textAlign="center"
              mt={8}
            >
              {header}
            </Typhography>
          )}
          {/* TODO: Replace with TextInput */}
          <SearchInput
            input={input}
            setInput={setInput}
            onClose={onClose}
            placeholder={placeholder}
            onSubmit={redirect}
          />
          {(showResults) && (
            <>
              <Scrollbar
                ref={scrollAreaRef}
                showGradient={isMobileDevice}
                style={{ maxHeight: scrollbarMaxHeight }}
              >
                <Box
                  pt={[0, 10, 10]}
                  pb={10}
                  px={[6, 8, 8]}
                >
                  {showHelpMessage && (
                    <HelpMessage
                      noMatchesMessage={noMatchesMessage}
                      noResultMessage={noResultMessage}
                      noMatches={!data?.items?.length}
                      searchQuery={input}
                    />
                  )}
                  {showResults && (
                    <SearchCardsContainer
                      items={data?.items.slice(0, visibleItems)}
                      noSearchMadeMessage={noSearchMadeMessage}
                      label={totalMatchesLabel}
                      searchQuery={input}
                      totalMatches={data?.totalMatching}
                      loading={loading}
                      loaders={3}
                    />
                  )}
                  {showMoreButton && (
                    <Box
                      width="full"
                      justifyContent="flex-end"
                      pt={7}
                      px={[6, 8, 8]}
                      pb={4}>
                      <TextButtonIcon
                        link={{ ...searchLink, href: url }}
                        direction="right">
                        <ArrowRight />
                      </TextButtonIcon>
                    </Box>
                  )}
                </Box>
              </Scrollbar>
            </>
          )}
        </Box>
      </div>
    </div>
  );
};

export default MainMenuSearch;
